import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Phone, CalendarCheck, BadgeDollarSign } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { sendChat, submitChatLead } from "../api";
import { CONTACT } from "../mock";

const getSessionId = () => {
  let id = localStorage.getItem("abjj_chat_session");
  if (!id) {
    id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("abjj_chat_session", id);
  }
  return id;
};

const SUGGESTIONS = [
  "Tell me about the $99 tryout",
  "What are the kids class times?",
  "How much is membership?",
  "I'm a total beginner — where do I start?",
];

const WELCOME = {
  role: "assistant",
  content:
    "Hi! Welcome to Alameda Brazilian Jiu-Jitsu \ud83e\udd4b I'm the academy's enrollment assistant. Are you looking at training for yourself, or for your child? I can help with programs, schedules, pricing and our $99 30-day new student tryout.",
  cta: false,
};

const ChatWidget = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", email: "", child_age: "", preferred_time: "" });
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  const openLeadForm = () => {
    setShowLead(true);
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !(lead.phone.trim() || lead.email.trim())) return;
    setLeadSubmitting(true);
    try {
      await submitChatLead({ ...lead, session_id: sessionId.current });
      setShowLead(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Thank you, ${lead.name.split(" ")[0]}! \ud83c\udf89 We've got your request and someone from the academy will reach out very soon to lock in your free class. You can also call us anytime at ${CONTACT.phone}.`,
          cta: false,
        },
      ]);
      setLead({ name: "", phone: "", email: "", child_age: "", preferred_time: "" });
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Sorry, something went wrong saving that. Please call us at ${CONTACT.phone} or email ${CONTACT.email} and we'll help right away!`, cta: false },
      ]);
      setShowLead(false);
    } finally {
      setLeadSubmitting(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await sendChat(sessionId.current, msg);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, cta: data.show_trial_cta }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Sorry, I'm having trouble right now. Please call us at ${CONTACT.phone} or email ${CONTACT.email} and we'll be happy to help!`,
          cta: false,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-20 right-4 lg:bottom-5 lg:right-5 z-[60] flex items-center gap-2 rounded-full shadow-xl transition-all duration-300 ${
          open ? "h-12 w-12 justify-center bg-[#1A1A1A]" : "h-14 pl-4 pr-5 bg-br-green hover:bg-[#00822f]"
        } text-white`}
        aria-label="Ask Alameda BJJ"
      >
        {open ? <X size={22} /> : (
          <>
            <MessageCircle size={22} />
            <span className="font-semibold text-sm hidden sm:inline">Ask Alameda BJJ</span>
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-36 right-4 lg:bottom-24 lg:right-5 z-[60] w-[calc(100vw-2rem)] sm:w-[380px] h-[540px] max-h-[calc(100vh-11rem)] bg-white rounded-2xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden animate-fade-up">
          {/* Header */}
          <div className="bg-br-green text-white px-5 py-4 flex items-center gap-3">
            <img src="/images/logo.png" alt="Alameda BJJ" className="h-9 w-9 rounded-full object-cover ring-2 ring-white/30" />
            <div className="leading-tight">
              <p className="font-display font-bold text-[15px]">Ask Alameda BJJ</p>
              <p className="text-[11px] text-green-50 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-br-yellow inline-block" /> Usually replies instantly
              </p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FBFBF9]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-br-green text-white rounded-br-sm"
                      : "bg-white border border-neutral-200 text-neutral-800 rounded-bl-sm"
                  }`}
                >
                  <span className="whitespace-pre-wrap">{m.content}</span>
                  {m.cta && (
                    <div className="mt-3 space-y-2">
                      <button
                        onClick={() => { setOpen(false); navigate("/tryout"); }}
                        data-testid="chat-tryout-cta"
                        className="flex items-center gap-2 w-full justify-center rounded-full bg-br-yellow text-[#1A1A1A] font-bold text-sm px-4 py-2 hover:bg-[#e6c900] transition-colors"
                      >
                        <BadgeDollarSign size={16} /> Enroll in the $99 Tryout
                      </button>
                      <button
                        onClick={openLeadForm}
                        data-testid="chat-free-class-cta"
                        className="flex items-center gap-2 w-full justify-center rounded-full bg-br-green text-white font-semibold text-sm px-4 py-2 hover:bg-[#00822f] transition-colors"
                      >
                        <CalendarCheck size={16} /> Book a Free Class
                      </button>
                      <button
                        onClick={() => { setOpen(false); navigate("/trial"); }}
                        className="w-full text-center text-[11px] text-neutral-400 hover:text-br-green"
                      >
                        or use the full booking page
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Suggestions (only before first user message) */}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-xs bg-white border border-neutral-200 rounded-full px-3 py-1.5 text-neutral-700 hover:border-br-green hover:text-br-green transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Inline lead capture form */}
            {showLead && (
              <div className="bg-white border border-br-green/30 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm text-[#1A1A1A]">Book your free class</p>
                  <button onClick={() => setShowLead(false)} className="text-neutral-400 hover:text-neutral-600"><X size={16} /></button>
                </div>
                <p className="text-[11px] text-neutral-500 mb-3">Leave your details and we'll reach out to confirm your spot.</p>
                <form onSubmit={submitLead} className="space-y-2">
                  <Input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name*" className="h-10 rounded-lg text-sm" />
                  <Input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone*" className="h-10 rounded-lg text-sm" />
                  <Input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email (optional)" className="h-10 rounded-lg text-sm" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={lead.child_age} onChange={(e) => setLead({ ...lead, child_age: e.target.value })} placeholder="Child's age" className="h-10 rounded-lg text-sm" />
                    <Input value={lead.preferred_time} onChange={(e) => setLead({ ...lead, preferred_time: e.target.value })} placeholder="Preferred time" className="h-10 rounded-lg text-sm" />
                  </div>
                  <Button type="submit" disabled={leadSubmitting || !lead.name.trim() || !(lead.phone.trim() || lead.email.trim())} className="w-full h-10 bg-br-green hover:bg-[#00822f] text-white rounded-lg font-semibold text-sm disabled:opacity-60">
                    {leadSubmitting ? "Sending..." : "Request My Free Class"}
                  </Button>
                  <p className="text-[10px] text-neutral-400 text-center">Name and a phone or email are required.</p>
                </form>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-neutral-100 p-3">
            {!showLead && (
              <button
                onClick={openLeadForm}
                className="mb-2 w-full flex items-center justify-center gap-2 rounded-full bg-br-yellow/90 text-[#1A1A1A] font-semibold text-sm px-4 py-2 hover:bg-br-yellow transition-colors"
              >
                <CalendarCheck size={15} /> Book a Free Class
              </button>
            )}
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Type your question..."
                className="flex-1 resize-none rounded-xl border border-neutral-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-br-green/40 max-h-24"
              />
              <Button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="h-10 w-10 p-0 rounded-xl bg-br-green hover:bg-[#00822f] text-white shrink-0 disabled:opacity-50"
                aria-label="Send"
              >
                <Send size={17} />
              </Button>
            </div>
            <a href={CONTACT.phoneHref} className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-neutral-400 hover:text-br-green">
              <Phone size={12} /> Prefer to talk? Call {CONTACT.phone}
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
